import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Browser } from "@capacitor/browser";
import { AuthService } from "@core/auth.service";
import { IonicModule } from "@ionic/angular";
import { Router } from "@angular/router";
import type { Data } from "@angular/router";
import type { Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";

@Component({
  selector: "app-tyviso-offers",
  templateUrl: "./tyviso-offers.component.html",
  styleUrls: ["./tyviso-offers.component.scss"],
  standalone: true,
  imports: [CommonModule, IonicModule],
})
export class TyvisoOffersComponent implements OnInit, OnDestroy {
  public loading = false;
  public errorMessage = "";
  public heroOffers: Data[] = [];
  public popularOffers: Data[] = [];
  public allOffers: Data[] = [];
  private expandedTerms = new Set<string>();
  private loadSub: Subscription;
  private authSub: Subscription;

  constructor(
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Wait for a real logged-in uid before calling the authenticated callable.
    this.authSub = this.authService
      .getUid$("TyvisoOffersComponent")
      .pipe(
        filter((userId) => !!userId && userId !== "null" && userId !== "undefined"),
        take(1),
      )
      .subscribe({
        next: () => this.loadOffers(),
        error: () => {
          this.errorMessage = "Please log in to view Tyviso offers.";
          this.loading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.authSub?.unsubscribe();
  }

  loadOffers(): void {
    this.loading = true;
    this.errorMessage = "";
    this.heroOffers = [];
    this.popularOffers = [];
    this.allOffers = [];
    this.expandedTerms.clear();

    // Empty payload is intentional: CF resolves sessionId from users/{uid}.referral_code.
    this.loadSub?.unsubscribe();
    this.loadSub = this.authService.getV2CloudFunction("tyvisorewardsproxyv2", {}).subscribe({
      next: (res: Data) => {
        const data = res?.data || res || {};
        this.heroOffers = Array.isArray(data?.hero) ? data.hero : [];
        this.popularOffers = Array.isArray(data?.popular) ? data.popular : [];
        this.allOffers = Array.isArray(data?.all) ? data.all : [];
        this.expandedTerms.clear();

        if (data?.status !== "success") {
          this.errorMessage = this.mapTyvisoError(data?.message);
        } else if (!this.heroOffers.length && !this.popularOffers.length && !this.allOffers.length) {
          this.errorMessage = "No offers available right now.";
        }
        this.loading = false;
      },
      error: () => {
        this.errorMessage = "Unable to load Tyviso offers. Please try again.";
        this.loading = false;
      },
    });
  }

  private mapTyvisoError(code: string): string {
    const apiErrors = [
      "tyviso_api_error",
      "tyviso_api_non_200",
      "missing_tyviso_config",
      "missing_session_id",
      "empty_offers",
    ];
    if (!code || apiErrors.includes(String(code))) {
      return "Unable to load Tyviso offers. Please try again.";
    }
    return String(code);
  }

  backToHomePage(): void {
    this.router.navigateByUrl("/mainTabs/tabs/home").catch(() => {
      // no-op
    });
  }

  openOffer(url: string): void {
    if (!url || !/^https?:\/\//i.test(url)) {
      return;
    }
    Browser.open({ url }).catch(() => {
      window.open(url, "_blank", "noopener,noreferrer");
    });
  }

  hasTerms(offer: Data): boolean {
    return typeof offer?.terms === "string" && offer.terms.trim().length > 0;
  }

  isTermsExpanded(section: string, offer: Data, index: number): boolean {
    return this.expandedTerms.has(this.termsKey(section, offer, index));
  }

  termsPanelId(section: string, offer: Data, index: number): string {
    return `tyviso-offers-terms-${this.termsKey(section, offer, index)}`;
  }

  toggleTerms(section: string, offer: Data, index: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.hasTerms(offer)) {
      return;
    }
    const key = this.termsKey(section, offer, index);
    if (this.expandedTerms.has(key)) {
      this.expandedTerms.delete(key);
    } else {
      this.expandedTerms.add(key);
    }
  }

  private termsKey(section: string, offer: Data, index: number): string {
    return `${section}:${String(offer?.id || offer?.url || index)}`;
  }
}
